import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "./lib/api.js";
import { ACCESS, AuthProvider, useAuth } from "./lib/auth.js";
import type { Role } from "./lib/types.js";
import { Shell } from "./ui/Shell.js";
import { Card, PageHeader } from "./ui/kit.js";
import { Accounting } from "./pages/Accounting.js";
import { Board } from "./pages/Board.js";
import { Conflicts } from "./pages/Conflicts.js";
import { CustomerDetail, Customers } from "./pages/Customers.js";
import { Dashboard } from "./pages/Dashboard.js";
import { InvoiceDetail, Invoices } from "./pages/Invoices.js";
import { JobDetail } from "./pages/JobDetail.js";
import { Jobs } from "./pages/Jobs.js";
import { Login } from "./pages/Login.js";
import { Materials } from "./pages/Materials.js";
import { Payments } from "./pages/Payments.js";
import { Performance } from "./pages/Performance.js";
import { Settings } from "./pages/Settings.js";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      /**
       * A 401 is handled by api.ts (it refreshes once, then ends the session), and a 403 is a
       * role the user does not have — retrying either just burns requests against a wall.
       */
      retry: (count, error) =>
        error instanceof ApiError && (error.status === 401 || error.status === 403)
          ? false
          : count < 2,
    },
    mutations: { retry: false },
  },
});

/** Mirrors the API's route middleware. The API is still the authority; this is just courtesy. */
function Require({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(...roles)) {
    return (
      <>
        <PageHeader title="ليس لديك صلاحية" />
        <Card>
          <p className="text-sm text-ink-soft">هذه الشاشة مخصّصة لأدوار أخرى في الشركة.</p>
        </Card>
      </>
    );
  }
  return <>{children}</>;
}

function Routed() {
  const { user } = useAuth();
  if (!user) return <Login />;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route path="board" element={<Board />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="materials" element={<Materials />} />
        <Route path="performance" element={<Performance />} />

        <Route
          path="conflicts"
          element={
            <Require roles={ACCESS.conflicts}>
              <Conflicts />
            </Require>
          }
        />
        <Route
          path="invoices"
          element={
            <Require roles={ACCESS.money}>
              <Invoices />
            </Require>
          }
        />
        <Route
          path="invoices/:id"
          element={
            <Require roles={ACCESS.money}>
              <InvoiceDetail />
            </Require>
          }
        />
        <Route
          path="payments"
          element={
            <Require roles={ACCESS.money}>
              <Payments />
            </Require>
          }
        />
        <Route
          path="accounting"
          element={
            <Require roles={ACCESS.money}>
              <Accounting />
            </Require>
          }
        />
        <Route
          path="settings"
          element={
            <Require roles={ACCESS.users}>
              <Settings />
            </Require>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routed />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
